/**
 * "Download PDF" on an invoice (#50 PR D): the invoice summary in the hauler's branding plus
 * one legacy-layout backup page per ticket. The API renders it on request and returns a
 * short-lived link, which opens in a new tab (the same pattern as the import file and data
 * export downloads).
 */

import React from 'react';
import { toast } from 'sonner';
import { Download, Loader2 } from 'lucide-react';
import { ApiError } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { useInvoicePdf } from './hooks';

export function DownloadInvoicePdf({ id }: { id: string }): React.JSX.Element {
  const pdf = useInvoicePdf(id);
  return (
    <Button
      variant="outline"
      disabled={pdf.isPending}
      onClick={(): void => {
        pdf.mutate(undefined, {
          onSuccess: (link) => {
            window.open(link.url, '_blank', 'noopener');
          },
          onError: (err) => {
            toast.error(
              err instanceof ApiError && err.code === 'INVOICE_PDF_UNAVAILABLE'
                ? 'The invoice PDF can’t be made right now. Try again in a few minutes.'
                : 'Couldn’t make the invoice PDF. Try again.',
            );
          },
        });
      }}
    >
      {pdf.isPending ? (
        <Loader2
          size={14}
          aria-hidden="true"
          style={{ marginRight: '6px' }}
          className="animate-spin"
        />
      ) : (
        <Download size={14} aria-hidden="true" style={{ marginRight: '6px' }} />
      )}
      Download PDF
    </Button>
  );
}
